import React from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import CookieConsent from '@/components/shared/CookieConsent';
import { FloatingContactButton } from '@/components/shared/FloatingContactButton';
import { ThemeToggle } from '@/components/shared/ThemeToggle';

export function PublicLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Scroll to top when route changes
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'What We Offer', path: '/services' },
    { name: 'Articles & Guides', path: '/resources' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Get Help', path: '/contact' },
  ];

  const legalLinks = [
    { name: 'Terms & Conditions', path: '/terms' },
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Billing & Refunds', path: '/refunds' },
    { name: 'Legal Disclaimer', path: '/disclaimer' },
    { name: 'Cookie Policy', path: '/cookies' },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b sticky top-0 z-50">
        <nav className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <img src="/logo.png" alt="Kosha Tax Logo" className="h-14 w-14 mr-3 object-contain" />
              <div className="text-2xl font-bold text-blue-600">Kosha Tax</div>
              <span className="hidden md:inline-block ml-3 text-sm text-gray-500 border-l pl-3">Precision in Every Return.</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navigation.map((item) => (
                <button
                  key={item.name}
                  onClick={() => navigate(item.path)}
                  className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {item.name}
                </button>
              ))}
            </div>

            {/* Auth Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <ThemeToggle />
              <Button variant="ghost" onClick={() => navigate('/login')}>
                Sign In
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center space-x-2">
              <ThemeToggle />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 space-y-2">
              {navigation.map((item) => (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                >
                  {item.name}
                </button>
              ))}
              <div className="flex flex-col space-y-2 px-4 pt-4 border-t">
                <Button variant="outline" onClick={() => navigate('/login')} className="w-full">
                  Sign In
                </Button>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-5 gap-8">
            {/* Company Info */}
            <div className="md:col-span-1">
              <div className="flex items-center mb-2">
                <img src="/logo.png" alt="Kosha Tax Logo" className="h-12 w-12 mr-3 object-contain" />
                <h3 className="text-xl font-bold">Kosha Tax</h3>
              </div>
              <p className="text-blue-400 text-sm italic mb-3">Precision in Every Return.</p>
              <p className="text-gray-400 text-sm">
                Tax filing and LLC formation made simple for everyone.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><button onClick={() => navigate('/about')} className="hover:text-white transition-colors">About Kosha Tax</button></li>
                <li><button onClick={() => navigate('/services')} className="hover:text-white transition-colors">What We Offer</button></li>
                <li><button onClick={() => navigate('/pricing')} className="hover:text-white transition-colors">Pricing</button></li>
                <li><button onClick={() => navigate('/contact')} className="hover:text-white transition-colors">Get Help</button></li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><button onClick={() => navigate('/services')} className="hover:text-white transition-colors">Tax Filing</button></li>
                <li><button onClick={() => navigate('/services')} className="hover:text-white transition-colors">LLC Formation</button></li>
                <li><button onClick={() => navigate('/resources')} className="hover:text-white transition-colors">Articles & Guides</button></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                {legalLinks.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path} className="hover:text-white transition-colors">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4">Get Help</h4>
              <p className="text-gray-400 text-sm mb-2">Have questions? We&apos;re here to help.</p>
              <p className="text-gray-400 text-sm">help@koshatax.com</p>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                &copy; {new Date().getFullYear()} Kosha Tax. All rights reserved.
              </p>
              <div className="flex items-center space-x-6 text-gray-400 text-sm">
                {legalLinks.map((link, index) => (
                  <React.Fragment key={link.path}>
                    <Link to={link.path} className="hover:text-white transition-colors">
                      {link.name}
                    </Link>
                    {index < legalLinks.length - 1 && <span className="hidden md:inline">|</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      <CookieConsent />

      {/* Floating Contact Button */}
      <FloatingContactButton />
    </div>
  );
}

export default PublicLayout;
